import React, { useEffect, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Collapse,
  FormGroup,
  UncontrolledTooltip,
  Accordion,
  AccordionItem,
  AccordionBody,
  Label,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import Lottie from "react-lottie"
import {
  booleanSorter,
  defaultPageSizeOptions,
  getLottieLoadingOptions,
  getLottieSearch,
  isTrue,
  showToast,
  stringSorter,
} from "utils/general"
import SelectGroupFilters from "components/DropdownList/SelectGroupFilters"
import TagsTree from "components/DropdownList/TagsTree"
import { clientsSetVideoNames, clientsTransferGroup, getClientsTable, updateClientsSettings } from "helpers/backend_helper"
import XLSX from "xlsx"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { Table as AntdTable, Input as AntdInput, Select, Popconfirm } from "antd"
import ClientDrawer from "./ClientDrawer"
import * as Yup from "yup"
import { useFormik } from "formik"
import { isEqual } from "lodash"
const { Search } = AntdInput

const ClientsManagementTable = props => {
  const [groups, setGroups] = useState(null)
  const [clients, setClients] = useState([])
  const [filteredClients, setFilteredClients] = useState([])
  const [action, setAction] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [selectedTags, setSelectedTags] = useState([])
  const [title] = props.t("Clients") //Excel and pdf name
  const [settingsModal, setSettingsModal] = useState(false)
  const [gridCols] = useState([4, 8])
  const [transferModal, setTransferModal] = useState(false)
  const [transferGroup, setTransferGroup] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [filtersOpen, setFiltersOpen] = useState("")
  const [videoMismatchFilter, setVideoMismatchFilter] = useState()
  const [matchConfirm, setMatchConfirm] = useState(false)

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = props.t("Clients") + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (groups) getClients()
  }, [groups])

  useEffect(() => {
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [groups])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, clients, videoMismatchFilter])

  // Handle search function
  const handleSearch = () => {
    let searchTextLowercase = searchText.toLowerCase()
    let result = clients.filter(
      data =>
        (!videoMismatchFilter || (videoMismatchFilter == '1' && data.name?.trim() == data.videoDisplayName?.trim()) || (videoMismatchFilter == '2' && data.videoDisplayName && data.name?.trim() != data.videoDisplayName?.trim())) &&
        (data.name?.toLowerCase().includes(searchTextLowercase) ||
          data.group?.toLowerCase().includes(searchTextLowercase) ||
          data.gsm?.toLowerCase().includes(searchTextLowercase) ||
          data.address?.toLowerCase().includes(searchTextLowercase))
    )
    setFilteredClients(result)
  }

  // Settings validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      gillieEnabled: "",
    },
    validationSchema: Yup.object({}),
    onSubmit: values => {
      // Get changed values compared to initial
      const initialFormValues = structuredClone(
        validation.initialValues
      )
      let temp = {}
      let changedProps = 0
      for (const key in validation.values) {
        if (!isEqual(initialFormValues[key], values[key])) {
          temp[key] = values[key]
          changedProps += 1
        }
      }

      if (selectedRowKeys && changedProps > 0 && !action) {
        setAction(true)
        updateClientsSettings(abortRef.current, selectedRowKeys, temp)
          .then(result => {
            console.log("Result: ", result)
            if (result.statusCode == 200) {
              showToast(props.t("Updated"), "success")
              validation.resetForm()
              setSettingsModal(false)
              getClients()
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            showToast(props.t("Error"), "error")
            console.log(err)
          })
          .finally(() => {
            setAction(false)
          })
      }
      else {
        showToast(props.t("Error"), "error")
      }
    },
  })

  // Get clients for table
  const getClients = () => {
    if (!loading) {
      setLoading(true)
      getClientsTable(abortRef.current, groups)
        .then(result => {
          //console.log("Clients result: ", result)
          if (result.statusCode == 200 && result.entries) {
            //result.entries.sort((a, b) => (a.name > b.name ? 1 : -1))
            for (const element of result.entries) {
              if (element.client_tags) {
                element.clientTagsString = element.client_tags.join(", ")
              }

            }
            setClients(result.entries)
          }
        })
        .catch(err => {
          console.log(err)
          setClients([])
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Move clients to a group
  const moveToGroup = () => {
    if (selectedRowKeys && transferGroup) {
      setAction(true)
      clientsTransferGroup(abortRef.current, selectedRowKeys, transferGroup)
        .then(result => {
          console.log("Move result: ", result)
          if (result.statusCode == 200) {
            setTransferModal(false)
            setSelectedRowKeys([])
            setSelectedRows([])
            getClients()
            showToast(props.t("Success"), "success")
          }
          else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setAction(false)
        })
    }
  }

  // Set matching video names to selected clients
  const setVideoNames = () => {
    if (selectedRowKeys) {
      setAction(true)
      clientsSetVideoNames(abortRef.current, selectedRowKeys)
        .then(result => {
          console.log("Result: ", result)
          if (result.statusCode == 200) {
            let message = "Updated " + result.updated + ", Skipped: " + result.skipped + ", Error: " + result.error
            setSelectedRowKeys([])
            setSelectedRows([])
            getClients()
            showToast(message, "info")
          }
          else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setAction(false)
        })
    }
  }

  // Table structure
  const columns = [
    {
      title: props.t("Actions"),
      dataIndex: "actions",
      align: "center",
      render: editFormatter,
      fixed: "left",
      width: "100px",
    },
    {
      dataIndex: "name",
      title: props.t("Name"),
      defaultSortOrder: "ascend",
      sorter: (a, b) => {
        return stringSorter(a, b, "name")
      },
      width: "140px",
      ellipsis: true,
    },
    /*{
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b) => {
        return stringSorter(a, b, "group")
      },
      width: "140px",
      ellipsis: true,
    },*/
    {
      dataIndex: "videoDisplayName",
      title: props.t("Video call display name"),
      sorter: (a, b) => {
        return stringSorter(a, b, "videoDisplayName")
      },
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "ssn",
      title: props.t("SSN"),
      sorter: (a, b) => {
        return stringSorter(a, b, "ssn")
      },
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "gillieEnabled",
      title: props.t("Gillie"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "gillieEnabled", sortOrder)
      },
      render: cell => {
        return cell ? <i className="mdi mdi-check-bold text-primary" /> : <></>
      },
      width: "140px",
      ellipsis: true,
    },
    /*{
      dataIndex: "gsm",
      title: props.t("GSM"),
      sorter: (a, b) => {
        return stringSorter(a, b, "gsm")
      },
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "address",
      title: props.t("Address"),
      sorter: (a, b) => {
        return stringSorter(a, b, "address")
      },
      width: "140px",
      ellipsis: true,
    },*/
    {
      dataIndex: "clientTagsString",
      title: props.t("Client tags"),
      sorter: (a, b) => {
        return stringSorter(a, b, "clientTagsString")
      },
      width: "200px",
      ellipsis: true,
    },
    /*{
      dataIndex: "gatewayKey",
      title: props.t("Gateway"),
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "trackerKey",
      title: props.t("Tracker"),
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "carephoneKey",
      title: props.t("Carephone"),
      width: "140px",
      ellipsis: true,
    },*/
    {
      dataIndex: "key",
      title: props.t("GUID"),
      width: "140px",
      ellipsis: true,
    },
  ]

  // Table formatting functions start
  function editFormatter(cell, row) {
    return (
      <>
        <Button
          color="primary"
          outline
          size="sm"
          onClick={() => {
            setShowDrawer(true)
            setSelectedRow(row)
          }}
        >
          <i className="mdi mdi-pencil" />
        </Button>
      </>
    )
  }

  // Table selection changed
  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRows(newSelectedRows)
  }

  // Export PDF table
  const exportPDF = () => {
    let message = props.t("PDF file exported succesfully!")
    const unit = "pt"
    const size = "A4" // Use A1, A2, A3 or A4
    const orientation = "landscape" // portrait or landscape
    const theme = "grid" //striped,  plain

    const marginLeft = 40
    const doc = new jsPDF(orientation, unit, size, theme)

    doc.setFontSize(14)

    const data = filteredClients.map(elt => [
      elt.name,
      elt.group,
      elt.gsm,
      elt.ssn,
      isTrue(elt.gillieEnabled),
    ])
    console.log(data, "pdf function")

    let content = {
      startY: 50,
      head: [
        [props.t("Name"), props.t("Group"), props.t("GSM"), props.t("SSN"), props.t("Gillie")],
      ],
      body: data,
    }

    doc.text(title, marginLeft, 40)
    doc.autoTable(content)
    doc.save(title + ".pdf")

    //Show toast
    showToast(message, "success")
  }

  // Creating Excel workbook+worksheet+download
  const downloadExcel = () => {
    let message = props.t("Excel file exported succesfully!")
    const wb = XLSX.utils.book_new()

    const Heading = [
      [props.t("Name"), props.t("Group"), props.t("GSM"), props.t("SSN"), props.t("Gillie")],
    ]
    const data = filteredClients.map(elt => [
      elt.name,
      elt.group,
      elt.gsm,
      elt.ssn,
      isTrue(elt.gillieEnabled),
    ])

    // creating sheet and adding data from 2nd row of column A.
    // Leaving first row to add Heading
    const ws = XLSX.utils.json_to_sheet(data, {
      origin: "A2",
      skipHeader: true,
    })
    XLSX.utils.sheet_add_aoa(ws, Heading, { origin: "A1" })
    XLSX.utils.book_append_sheet(wb, ws, title)
    XLSX.write(wb, { bookType: "xlsx", type: "buffer" })
    XLSX.writeFile(wb, title + " (" + new Date() + ")" + ".xlsx")

    //Show toast
    showToast(message, "success")
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={props.t("Clients")} />

          <Row>
            <Col className="mb-3" style={{ minWidth: "200px", maxWidth: "400px" }}>
              <TagsTree
                loadData={true}
                expanded={true}
                selectionChanged={setSelectedTags}
              />
            </Col>
            <Col className="mb-3" style={{ minWidth: "300px", /*maxWidth: "400px"*/ }}>
              <SelectGroupFilters
                onChange={setGroups}
                multiple={true}
                selectedTags={selectedTags}
              />
            </Col>
          </Row>

          {!groups && !loading && (
            <Card>
              <CardBody>
                <div className="text-center my-5">
                  <Lottie
                    options={getLottieSearch()}
                    height={150}
                    width={180}
                  ></Lottie>
                </div>
              </CardBody>
            </Card>
          )}

          {groups && (
            <div>
              <Card>
                <CardBody>
                  {clients != null && (
                    <React.Fragment>
                      <Row>
                        <Col className="mb-3" style={{ maxWidth: "320px" }}>
                          <Search
                            placeholder={props.t("Search")}
                            allowClear
                            value={searchText}
                            onChange={event => {
                              setSearchText(event.target.value)
                            }}
                            style={{ minWidth: "240px" }}
                          />
                        </Col>
                        <Col>
                          <Button
                            color="primary"
                            className="compact-button"
                            id="filter-button"
                            outline
                            onClick={() => {
                              filtersOpen.length > 0
                                ? setFiltersOpen("")
                                : setFiltersOpen("1")
                            }}
                          >
                            <UncontrolledTooltip
                              placement="bottom"
                              target="filter-button"
                            >
                              {props.t("Filters")}
                            </UncontrolledTooltip>
                            {filtersOpen ? (
                              <i className="mdi mdi-filter-minus"></i>
                            ) : (
                              <i className="mdi mdi-filter-menu"></i>
                            )}
                          </Button>
                          <Button
                            className="compact-button ms-3"
                            color="primary"
                            disabled={loading}
                            onClick={() => {
                              getClients()
                            }}
                          >
                            <i className="mdi mdi-refresh me-2" />
                            {props.t("Refresh")}
                          </Button>
                        </Col>
                        <Col>
                          <span className="float-end ms-2">
                            <UncontrolledTooltip
                              placement="bottom"
                              target="downloadstatstable"
                            >
                              {props.t("Download statistics")}
                            </UncontrolledTooltip>
                            <Dropdown
                              id="printButtonServicesStats"
                              isOpen={menu}
                              toggle={() => setMenu(!menu)}
                              className="download-btn text-center"
                              type="button"
                            >
                              <DropdownToggle
                                id="downloadstatstable"
                                className="text-muted"
                                tag="i"
                              >
                                <i className="mdi mdi-download" />
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={() => downloadExcel()}>
                                  {props.t("Download .xlsx")}
                                </DropdownItem>
                                <DropdownItem onClick={() => exportPDF()}>
                                  {props.t("Download .pdf")}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </span>
                          <span className="mt-2 float-end">
                            <p>
                              {filteredClients.length} {props.t("clients")}
                            </p>
                          </span>
                        </Col>
                      </Row>

                      <Accordion
                        flush
                        open={filtersOpen}
                        toggle={id => {
                          id === filtersOpen
                            ? setFiltersOpen("")
                            : setFiltersOpen(id)
                        }}
                      >
                        <AccordionItem>
                          <AccordionBody
                            accordionId="1"
                            className="mb-3"
                            style={{
                              //backgroundColor: "#e8f0fe",
                              //border: "1px solid #ced4da",
                              border: "1px solid",
                              borderRadius: "4px",
                            }}
                          >
                            <Row className="mb-3">
                              <Col className="col-auto">
                                <span
                                  className="font-size-16 text-primary"
                                  style={{ fontWeight: "500" }}
                                >
                                  <i className="mdi mdi-filter me-1"></i>
                                  {props.t("Filters")}
                                </span>
                              </Col>
                              <Col>
                                <Button
                                  color="danger"
                                  outline
                                  size="sm"
                                  onClick={() => {
                                    setVideoMismatchFilter()
                                  }}
                                >
                                  <i className="mdi mdi-close me-1"></i>
                                  {props.t("Clear")}
                                </Button>
                              </Col>
                            </Row>
                            <Row xs={1} sm={2} md={3} xl={4}>
                              <Col className="mb-3">
                                <Select
                                  placeholder={props.t("Video call display name")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={videoMismatchFilter}
                                  onChange={(v) => { setVideoMismatchFilter(v) }}
                                  options={[
                                    { value: '1', label: props.t("Sama kuin asiakkaan nimi") },
                                    { value: '2', label: props.t("Eri kuin asiakkaan nimi") },
                                  ]}
                                />
                              </Col>
                            </Row>
                          </AccordionBody>
                        </AccordionItem>
                      </Accordion>

                      <Collapse isOpen={selectedRowKeys.length > 0}>
                        <Row>
                          <Col className="mb-3 col-auto">
                            <Button
                              color="primary"
                              className="compact-button"
                              disabled={selectedRowKeys.length == 0 || loading || action}
                              onClick={() => {
                                setTransferModal(true)
                              }}
                            >
                              <i className="mdi mdi-account-group me-2" />
                              {props.t("Move to group") + "..."}
                            </Button>
                            <Button
                              color="primary"
                              className="compact-button ms-3"
                              disabled={selectedRowKeys.length == 0 || loading || action}
                              onClick={() => {
                                setSettingsModal(true)
                              }}
                            >
                              <i className="mdi mdi-cog me-2" />
                              {props.t("Change settings")}
                            </Button>
                            <Popconfirm
                              title={props.t("Aseta valittujen asiakkaiden nimi videon näyttönimeksi?")}
                              open={matchConfirm}
                              okType="success"
                              okText={props.t("Confirm")}
                              cancelText={props.t("Cancel")}
                              onConfirm={() => {
                                setVideoNames()
                                setMatchConfirm(false)
                              }}
                              onCancel={() => {
                                setMatchConfirm(false)
                              }}
                            >
                              <Button
                                color="primary"
                                className="compact-button ms-3"
                                disabled={selectedRowKeys.length == 0 || loading || action}
                                onClick={() => {
                                  setMatchConfirm(true)
                                }}
                              >
                                <i className="mdi mdi-account-group me-2" />
                                {props.t("Aseta videonimi")}
                              </Button>
                            </Popconfirm>
                          </Col>
                          <Col className="mt-2">
                            <span id="selections-text">
                              {selectedRowKeys.length +
                                " " +
                                props.t("selected clients")}
                            </span>
                          </Col>
                        </Row>
                      </Collapse>

                      {!loading && (
                        <>
                          <div className="mt-1">
                            <AntdTable
                              className="colored-header narrow-header"
                              bordered
                              size="small"
                              rowSelection={{
                                selectedRowKeys,
                                onChange: onSelectChange,
                              }}
                              columns={columns}
                              dataSource={filteredClients}
                              pagination={{
                                showSizeChanger: true,
                                defaultPageSize: 100,
                                pageSizeOptions: defaultPageSizeOptions,
                              }}
                              tableLayout="auto"
                              scroll={{
                                x: "max-content",
                                y: "70vh",
                              }}
                            />
                          </div>
                        </>
                      )}
                      {loading && (
                        <Lottie
                          options={getLottieLoadingOptions()}
                          height={100}
                          width={100}
                        ></Lottie>
                      )}
                    </React.Fragment>
                  )}
                </CardBody>
              </Card>
            </div>
          )}

          <ClientDrawer
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            selectedRow={selectedRow}
            getClients={getClients}
          />

          <Modal
            isOpen={transferModal}
            toggle={() => {
              setTransferModal(!transferModal)
            }}
            centered
          >
            <ModalHeader>{props.t("Move to group")}</ModalHeader>
            <Form
              className="form-horizontal"
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <ModalBody>
                <div className="mb-3">
                  {selectedRows.map((entry, index) => (
                    <span
                      key={"" + index}
                      className="me-1 mb-1 badge badge-soft-primary font-size-12"
                    >
                      {entry.name}
                    </span>
                  ))}
                </div>
                <FormGroup>
                  <SelectGroupFilters
                    onChange={setTransferGroup}
                    multiple={false}
                    clearable={false}
                  //selectedTags={selectedTags}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  outline
                  onClick={() => {
                    setTransferModal(false)
                  }}
                >
                  {props.t("Cancel")}
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    moveToGroup()
                  }}
                  disabled={action || !transferGroup || !selectedRowKeys}
                >
                  {props.t("Move")}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/** Change settings modal */}
          <Modal
            isOpen={settingsModal}
            toggle={() => {
              setSettingsModal(!settingsModal)
            }}
            centered
          >
            <ModalHeader>{props.t("Change settings")}</ModalHeader>
            <Form
              className="form-horizontal"
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <ModalBody>
                <div className="mb-3">
                  {selectedRows.map((entry, index) => (
                    <span
                      key={"" + index}
                      className="me-1 mb-1 badge badge-soft-primary font-size-12"
                    >
                      {entry.name}
                    </span>
                  ))}
                </div>
                <Row xs={1}>
                  <Col>
                    <FormGroup row className="align-items-center">
                      <Label sm={gridCols[0]}>
                        {props.t("Gillie")}
                      </Label>
                      <Col sm={gridCols[1]}>
                        <Select
                          value={validation.values.gillieEnabled}
                          onChange={v => {
                            validation.setFieldValue(
                              "gillieEnabled",
                              v
                            )
                          }}
                          options={[
                            { label: props.t("Undefined"), value: "" },
                            { label: props.t("Enabled"), value: true },
                            { label: props.t("Disabled"), value: false },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  outline
                  onClick={() => {
                    setSettingsModal(false)
                  }}
                >
                  {props.t("Cancel")}
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    validation.submitForm()
                  }}
                  disabled={action || !selectedRowKeys}
                >
                  {props.t("Set")}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

        </Container>
      </div>
    </>
  )
}

ClientsManagementTable.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(ClientsManagementTable)
